import { useQueryClient } from '@tanstack/react-query'
import { Popover } from 'antd'
import { useAtom } from 'jotai'
import { useMemo, useState, type FC } from 'react'
import { FaUser } from 'react-icons/fa'

import { Button } from '@/components/elements'
import { LABELS } from '@/constants/common/message.constants'
import { AvailableDcSelect } from '@/features/dcs/components'
import { useAvailableDcs } from '@/features/dcs/hooks'
import { type UserMeExEntity, type UserMeTenantExEntity } from '@/libs/api'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { AuthUserPopover } from '../AuthUserPopover'

type Props = {
  authUser: UserMeExEntity
  currentTenant: UserMeTenantExEntity
  onLogout: () => void
}

export const AuthUserPopoverTrigger: FC<Props> = ({
  authUser,
  currentTenant,
  onLogout
}) => {
  const [selectedDcId, setSelectedDcId] = useAtom(selectedDcIdAtom)
  const { data } = useAvailableDcs(currentTenant.key, authUser)
  const currentDcName = useMemo(
    () => data.find((x) => x.id == selectedDcId)?.name,
    [data, selectedDcId]
  )

  const [isOpenDcSelect, setIsOpenDcSelect] = useState(false)

  const queryClient = useQueryClient()
  // 選択中DC基準でフィルタされるマスタ(プルダウン等)が古いDCのキャッシュで残らないよう、
  // 拠点切替時にTanStack Queryのキャッシュを全クリアし、各画面のクエリを再フェッチさせる
  const handleChangeDc = (value: number | null) => {
    const newId = value ?? 0
    if (newId === selectedDcId) return
    setSelectedDcId(newId)
    queryClient.clear()
  }

  return (
    <div className="flex items-center gap-2">
      <Popover
        trigger={['hover']}
        placement="bottomRight"
        arrow={false}
        open={isOpenDcSelect}
        onOpenChange={(newOpen) => setIsOpenDcSelect(newOpen)}
        content={
          <>
            <div className="pb-1 text-2xs text-secondary">{LABELS.DC}</div>
            <AvailableDcSelect
              tenantKey={currentTenant.key}
              authUser={authUser}
              value={selectedDcId}
              showSearch
              onChange={handleChangeDc}
              onSelect={() => setIsOpenDcSelect(false)}
            />
          </>
        }
      >
        <div className="min-w-[100px] pb-2 leading-none text-secondary">
          <div className="pb-1 text-2xs ">{LABELS.DC}</div>
          <div>{currentDcName}</div>
        </div>
      </Popover>
      <Popover
        trigger={['hover']}
        placement="bottomRight"
        arrow={false}
        content={
          <AuthUserPopover
            authUser={authUser}
            currentTenant={currentTenant}
            onLogout={onLogout}
          />
        }
      >
        {/* 
        Warning: findDOMNode is deprecated and will be removed in the next major release が発生するので Popoverの子要素にdivを挟む
        - https://github.com/ant-design/ant-design/issues/48709
      */}
        <div className="inline-block">
          <Button
            type="primary"
            className="flex h-[38px] w-[38px] items-center justify-center rounded-md p-0"
            data-testid="auth-user-popover-trigger"
          >
            <FaUser className="text-xs" />
          </Button>
        </div>
      </Popover>
    </div>
  )
}
